import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Image, Button } from 'react-bootstrap';
import { faLeaf, faHeart, faAward, faUsers,faClock,faMapMarkerAlt,faPhone } from '@fortawesome/free-solid-svg-icons';
import './pages.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import teamMembers from '../data/team'; // Your team data
import 'animate.css';
import axios from 'axios';
import { API } from '../config/api';

const AboutPage = () => {
  const [storeSettings, setStoreSettings] = useState({
    storeAddress: '123 Food Street, Lagos',
    storePhone: '+234 700 NOUREESH',
    storeEmail: 'hello@noureeshfoods.com',
    openingTime: '9:00 AM',
    closingTime: '9:00 PM',
    openingHours: {
      weekday: '9am - 9pm',
      sunday: '10am - 6pm'
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const formatTime = (timeString) => {
      if (!timeString) return '9:00 AM';
      
      try {
        const [hours, minutes] = timeString.split(':');
        const hourInt = parseInt(hours, 10);
        const period = hourInt >= 12 ? 'PM' : 'AM';
        const hour12 = hourInt % 12 || 12; // Convert 0 or 24 to 12
        return `${hour12}:${minutes || '00'} ${period}`;
      } catch (error) {
        console.error('Error formatting time:', error);
        return '9:00 AM';
      }
    };

    const fetchStoreSettings = async () => {
      try {
          const response = await axios.get(`${API}/settings`);
        if (response.data && response.data.store) {
          const { storeAddress, storePhone, storeEmail, openingTime, closingTime } = response.data.store;
          
          // Format opening hours
          const formattedOpeningHours = {
            weekday: `${formatTime(openingTime || '09:00')} - ${formatTime(closingTime || '21:00')}`,
            sunday: `10:00 AM - 6:00 PM` // Default Sunday hours
          };
          
          setStoreSettings({
            storeAddress: storeAddress || '123 Food Street, Off Culinary Avenue, Lagos',
            storePhone: storePhone || '+234 700 NOUREESH',
            storeEmail: storeEmail || 'hello@noureeshfoods.com',
            openingTime: openingTime ? formatTime(openingTime) : '9:00 AM',
            closingTime: closingTime ? formatTime(closingTime) : '9:00 PM',
            openingHours: formattedOpeningHours
          });
        }
      } catch (error) {
        console.error('Error fetching store settings:', error);
        // Keep default values if API call fails
      } finally {
        setLoading(false);
      }
    };

    fetchStoreSettings();
  }, []);

  if (loading) {
    return (
      <div className="about-page">
        <div className="text-center py-5">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero bg-light py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-3 animate__animated animate__fadeIn">
                Our Story
              </h1>
              <p className="lead animate__animated animate__fadeIn animate__delay-1s">
                From a small family kitchen to Lagos' favorite homemade food destination
              </p>
              <Button 
                variant="danger" 
                size="lg" 
                className="mt-3 animate__animated animate__fadeIn animate__delay-2s"
                href="/menu"
              >
                Explore Our Menu
              </Button>
            </Col>
            <Col lg={6}>
              <Image 
                src="https://images.unsplash.com/photo-1606787366850-de6330128bfc" 
                alt="Noureesh Foods Kitchen" 
                fluid 
                rounded 
                className="shadow-lg animate__animated animate__fadeIn"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="py-5 bg-white">
        <Container>
          <h2 className="text-center mb-5 fw-bold">Our Promise to You</h2>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4 hover-scale">
                <div className="icon-wrapper bg-danger bg-opacity-10 text-danger mx-auto mb-3">
                  <FontAwesomeIcon icon={faLeaf} size="2x" />
                </div>
                <Card.Title>100% Natural</Card.Title>
                <Card.Text>
                  We use only the freshest ingredients with no artificial additives
                </Card.Text>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4 hover-scale">
                <div className="icon-wrapper bg-danger bg-opacity-10 text-danger mx-auto mb-3">
                  <FontAwesomeIcon icon={faHeart} size="2x" />
                </div>
                <Card.Title>Made with Love</Card.Title>
                <Card.Text>
                  Every dish is prepared with the same care we'd serve our own family
                </Card.Text>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm text-center p-4 hover-scale">
                <div className="icon-wrapper bg-danger bg-opacity-10 text-danger mx-auto mb-3">
                  <FontAwesomeIcon icon={faAward} size="2x" />
                </div>
                <Card.Title>Family Recipes</Card.Title>
                <Card.Text>
                  Time-tested recipes passed down through generations
                </Card.Text>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* History Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0 order-lg-2">
              <h2 className="fw-bold mb-4">From Humble Beginnings</h2>
              <p className="lead">
                Founded in 2024 by Mama Noureesh, our journey began in a small home kitchen in Surulere
              </p>
              <p>
                What started as supplying homemade snacks to local offices has grown into Lagos' most 
                trusted food delivery service, while maintaining our commitment to authentic homemade taste.
              </p>
              <div className="d-flex align-items-center mt-4">
                <div className="bg-danger rounded-circle p-3 me-3">
                  <FontAwesomeIcon icon={faUsers} size="2x" className="text-white" />
                </div>
                <div>
                  <h4 className="mb-0">50,000+</h4>
                  <p className="text-muted mb-0">Satisfied Customers</p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="order-lg-1">
              <div className="position-relative">
                <Image 
                  src="https://images.unsplash.com/photo-1556911220-bff31c812dba" 
                  alt="Original Noureesh Kitchen" 
                  fluid 
                  rounded 
                  className="shadow mb-3"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Team Section */}
      <section className="py-5 bg-white">
        <Container>
          <h2 className="text-center mb-5 fw-bold">Meet Our Family</h2>
          <Row>
            {teamMembers.map((member, index) => (
              <Col md={6} lg={4} className="mb-4 d-flex" key={index}>
                <Card className="h-100 w-100 border-0 text-center team-card d-flex flex-column align-items-center justify-content-between" style={{ minHeight: '100%' }}>
                  <div className="team-img-wrapper mx-auto mt-4 mb-3">
                    <Image 
                      src={member.image} 
                      alt={member.name} 
                      roundedCircle 
                      fluid
                    />
                  </div>
                  <Card.Body className="d-flex flex-column align-items-center">
                    <Card.Title>{member.name}</Card.Title>
                    <Card.Subtitle className="text-danger mb-3">{member.role}</Card.Subtitle>
                    <Card.Text>{member.bio}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Visit Us Section */}
      <section className="py-5 bg-danger text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="fw-bold mb-4">Visit Our Kitchen</h2>
              <div className="d-flex mb-3">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-3 mt-1" />
                <div>
                  <h5>Our Location</h5>
                  <p>{storeSettings.storeAddress}</p>
                </div>
              </div>
              <div className="d-flex mb-3">
                <FontAwesomeIcon icon={faClock} className="me-3 mt-1" />
                <div>
                  <h5>Opening Hours</h5>
                  <p>Monday - Saturday: {storeSettings.openingHours.weekday}</p>
                  <p>Sunday: {storeSettings.openingHours.sunday}</p>
                </div>
              </div>
              <div className="d-flex">
                <FontAwesomeIcon icon={faPhone} className="me-3 mt-1" />
                <div>
                  <h5>Contact Us</h5>
                  <p>{storeSettings.storePhone}</p>
                  <p>{storeSettings.storeEmail}</p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="ratio ratio-16x9">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.123456789012!2d3.1234567890123456!3d6.123456789012345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMDcnMjQuNSJOIDPCsDA3JzI0LjUiRQ!5e0!3m2!1sen!2sng!4v1234567890123!5m2!1sen!2sng" 
                  style={{ border: 0 }}
                  allowFullScreen="" 
                  loading="lazy"
                  title="Noureesh Foods Location"
                ></iframe>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AboutPage;